/**
 * SourceExfilGuard.jsx — Phase 79 · relaxed in Phase 84
 *
 * The original guard blocked right-click AND flashed a red "Right-click is
 * disabled" toast on every context-menu, which turned out to interfere
 * with regular workflow — several users reported that basic interactions
 * felt broken or that they were losing right-click productivity gestures
 * (open link in new tab, inspect, back/forward on trackpads that emit a
 * context event).
 *
 * NEW BEHAVIOUR:
 *   - Founders: totally exempt (unchanged).
 *   - Everyone else:
 *       • Ctrl/⌘+S (save page) → blocked + logged + toast
 *       • Ctrl/⌘+U (view source) → blocked + logged + toast
 *       • Ctrl/⌘+Shift+I/J/C, F12 (DevTools) → blocked + logged + toast
 *       • Right-click / context menu → ALLOWED. We SILENTLY log via a
 *         cool-down (once per 10 min per route) so if abuse spikes we can
 *         still audit — but no toast, no preventDefault, no UX friction.
 *       • Ctrl/⌘+P (print), Copy → allowed, silently un-instrumented.
 *
 * Nothing here ever calls preventDefault on a regular left-click or link
 * navigation — those were never intercepted; if links were failing to
 * open it was because the destination route was wrong (see Phase 84
 * approvals-link fix), NOT because of this file.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const notify = (action, route) => {
  try {
    api.post("/security/exfil-attempt", {
      action,
      route,
      user_agent: navigator.userAgent,
    }).catch(() => {});
  } catch { /* swallow */ }
};

const warn = (label) => {
  toast.error(`${label} is disabled. This attempt has been logged for security review.`, { id: "exfil-warn" });
};

const CONTEXT_LOG_COOLDOWN_MS = 10 * 60 * 1000;   // 10 min per route

export default function SourceExfilGuard() {
  const { user } = useAuth();
  const location = useLocation();
  const lastContextLogAt = useRef({});   // { route: ms_timestamp }

  useEffect(() => {
    if (!user || user.role === "founder") return;

    const route = location.pathname + location.search;

    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      const mod = e.metaKey || e.ctrlKey;
      // Save page
      if (mod && key === "s") { e.preventDefault(); notify("save_page", route); warn("Save page"); return; }
      // View source
      if (mod && key === "u") { e.preventDefault(); notify("view_source", route); warn("View source"); return; }
      // DevTools — F12
      if (key === "f12")      { e.preventDefault(); notify("devtools_f12", route); warn("DevTools"); return; }
      // DevTools — Ctrl/⌘+Shift+I / J / C
      if (mod && e.shiftKey && ["i", "j", "c"].includes(key)) {
        e.preventDefault(); notify(`devtools_${key}`, route); warn("DevTools"); return;
      }
      // NOTE: Ctrl/⌘+P (print) intentionally left un-intercepted — users
      // legitimately print requisitions / approval summaries. Copy also
      // left alone (no bulk_copy heuristic anymore — it produced false
      // positives on wide table selections).
    };

    const onContextMenu = () => {
      // Phase 84 — DO NOT preventDefault, DO NOT toast. Silently log at
      // most once every 10 minutes per route so we still have an audit
      // trail if someone genuinely tries to exfiltrate via a right-click
      // "Save as…" spree, without punishing normal usage.
      const now = Date.now();
      const last = lastContextLogAt.current[route] || 0;
      if (now - last >= CONTEXT_LOG_COOLDOWN_MS) {
        lastContextLogAt.current[route] = now;
        notify("context_menu", route);
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("contextmenu", onContextMenu, { capture: false, passive: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("contextmenu", onContextMenu, { capture: false });
    };
  }, [user, location.pathname, location.search]);

  return null;
}
